/**
 * Mode Selectors - Question Selection Strategies
 *
 * Mode-specific question selection logic.
 * Each selector implements a different strategy for choosing questions.
 *
 * §4.3 STABILIZATION: Extracted shared filtering helpers to reduce
 * duplication across mode selectors.
 *
 * NOTE: getWeakestFamilies and getAvailableChallengeFamilies have been
 * consolidated into masteryCanonical.ts (Step 1.5.5).
 */

import { type Question, getFamilyDefinition, TAXONOMY } from '../content';
import { AppState } from '../app/types';
import { GroupId } from '../config/systemConfig';
import { FluencyConfig, SprintConfig, ChallengeConfig, ExperimentsConfig } from './types';
import { createRng, type SeededRng } from '../utils/deterministicRng';
import { getLDG, calculateFrontier } from '../governance';
import type { FamilyMasteryInput } from '../governance';
import { getFamilyMasteryKey } from '../mastery';
import { warn as logWarn } from '../utils/logger';

// ─── Shared Helpers (LL-1, LL-10) ────────────────────────────────────────────

/**
 * Build FamilyMasteryInput[] from familyMastery state for LDG frontier calculation.
 * Shared between MissionBriefing and modeSelectors to avoid duplication (LL-1).
 */
export function buildFrontierMasteryInputs(familyMastery: Record<string, {
  masteryScore: number;
  attempts: number;
}>, selectedGroup: GroupId): FamilyMasteryInput[] {
  return TAXONOMY.map(family => {
    const key = getFamilyMasteryKey(family.familyId, selectedGroup);
    const m = familyMastery[key];
    const score = m?.masteryScore ?? 0;
    return {
      familyId: family.familyId,
      masteryScore: score,
      attempts: m?.attempts ?? 0,
      isMastered: score >= 80
    };
  });
}

/**
 * Detect Fluency cold-start: no families with 50-80% mastery for the selected group.
 * Shared between LearningLabPage (UI banner) and modeSelectors (question selection) (LL-10).
 */
export function isFluencyColdStart(familyMastery: Record<string, {
  masteryScore: number;
  attempts?: number;
}>, selectedGroup: GroupId): boolean {
  return !Object.entries(familyMastery).some(([key, m]) => {
    if (!key.endsWith(`_g${selectedGroup}`)) return false;
    return m.masteryScore >= 50 && m.masteryScore <= 80;
  });
}

// ─── §4.3 Shared Filtering Helpers ───────────────────────────────────────────

/**
 * Filter questions to those matching the selected group.
 * This is the fundamental group-gating filter used by all modes.
 */
function filterByGroup(questions: Question[], selectedGroup: GroupId): Question[] {
  return questions.filter(q => q.group === selectedGroup);
}

/**
 * Filter questions to those within a tier range.
 */
function filterByTierRange(questions: Question[], min: number, max: number): Question[] {
  return questions.filter(q => q.tier >= min && q.tier <= max);
}

/**
 * Filter questions to a specific family within a group.
 */
function filterByFamilyAndGroup(questions: Question[], familyId: string, selectedGroup: GroupId): Question[] {
  return questions.filter(q => q.familyId === familyId && q.group === selectedGroup);
}

/**
 * Unbiased Fisher-Yates shuffle that returns a NEW array.
 *
 * Replaces the biased `.sort(() => rng.random() - 0.5)` pattern that was
 * previously used in fluency/sprint/experiments selectors. `Array.prototype.sort`
 * does not guarantee a uniform distribution when given a non-transitive
 * comparator, so it should never be used for randomization.
 */
function shuffle<T>(items: T[], rng: SeededRng): T[] {
  return rng.shuffle(items);
}

/**
 * Fluency Mode: Mixed practice from multiple modules
 * Focus on speed and accuracy with familiar content
 */
export function getFluencyQuestions(config: FluencyConfig, allQuestions: Question[], state: AppState, selectedGroup: GroupId): Question[] {
  const rng = createRng(Date.now());
  const familyMastery = state.familyMastery || {};

  // §4.3: Use shared group filter
  let candidates = filterByGroup(allQuestions, selectedGroup);

  // Module filter
  const targetModules = config.targetModules;
  if (targetModules && targetModules.length > 0) {
    candidates = candidates.filter(q => targetModules.includes(q.module));
  }

  // §4.3: Use shared tier filter
  if (config.tierRange) {
    candidates = filterByTierRange(candidates, config.tierRange.min, config.tierRange.max);
  }

  // Filter to fluency-eligible families only (exclude reading comprehension, reasoning tasks)
  const eligibleCandidates = candidates.filter(q => {
    const family = getFamilyDefinition(q.familyId);
    return family?.fluencyEligible !== false;
  });
  if (eligibleCandidates.length > 0) {
    candidates = eligibleCandidates;
  }

  // Prioritize families with 50-80% mastery (fluency sweet spot) in the CURRENT group only.
  // FIX F-MAS-R3-FLUENCY-MODE-CROSS-GROUP-LEAK: filter on the group suffix so a
  // family with sweet-spot mastery in group 6 doesn't leak into a group 7 fluency session.
  const groupSuffix = `_g${selectedGroup}`;
  const fluencyFamilies = Object.entries(familyMastery).filter(([key, m]) => key.endsWith(groupSuffix) && m.masteryScore >= 50 && m.masteryScore <= 80).map(([key]) => key.slice(0, -groupSuffix.length));
  if (fluencyFamilies.length > 0) {
    const fluencyQuestions = candidates.filter(q => fluencyFamilies.includes(q.familyId));
    if (fluencyQuestions.length > 0) {
      candidates = fluencyQuestions;
    }
  } else {
    // ── Cold Start: Use LDG frontier for didactically appropriate starting families ──
    // When no families have 50-80% mastery (fresh student), use the LDG frontier
    // to select families that are didactically appropriate starting points,
    // rather than picking randomly from ALL eligible questions.
    try {
      const masteryInputs: FamilyMasteryInput[] = buildFrontierMasteryInputs(familyMastery, selectedGroup);
      const frontierResult = calculateFrontier(getLDG(), masteryInputs, {
        maxFrontierSize: 20,
        masteryThreshold: 80
      });
      if (frontierResult.frontier.length > 0) {
        const frontierFamilyIds = new Set(frontierResult.frontier.map(f => f.familyId));
        const frontierQuestions = candidates.filter(q => frontierFamilyIds.has(q.familyId));
        if (frontierQuestions.length > 0) {
          candidates = frontierQuestions;
        }
      }
    } catch (err) {
      // If LDG calculation fails, fall back to all eligible candidates.
      // Surface as warn so RC monitoring can spot regressions in the frontier engine.
      logWarn('[modes/fluency] LDG frontier cold-start failed, falling back to all eligible candidates', err);
    }
  }

  // Estimate questions based on duration (30 seconds per question)
  const estimatedCount = Math.floor(config.duration / 30);
  const targetCount = Math.min(estimatedCount, candidates.length, 50);

  // Shuffle and select (unbiased Fisher-Yates)
  const shuffled = shuffle(candidates, rng);
  return shuffled.slice(0, targetCount);
}

/**
 * Sprint Mode: Focused practice on weakest family
 * Intensive drilling on a single skill
 */
export function getSprintQuestions(config: SprintConfig, allQuestions: Question[], state: AppState, selectedGroup: GroupId): Question[] {
  const rng = createRng(Date.now());

  // §4.3: Use shared family+group filter
  let candidates = filterByFamilyAndGroup(allQuestions, config.targetFamilyId, selectedGroup);
  if (candidates.length === 0) {
    throw new Error(`No questions found for family ${config.targetFamilyId}`);
  }

  // If adaptive, start with easier questions
  if (config.adaptive) {
    candidates = candidates.slice().sort((a, b) => a.tier - b.tier);
  } else {
    // Random order (unbiased Fisher-Yates)
    candidates = shuffle(candidates, rng);
  }

  // Warming-up: prepend 2 easy questions (tier ≤ 3) before the main set
  if (config.warmup) {
    const warmupPool = candidates.filter(q => q.tier <= 3);
    const mainPool = candidates.filter(q => q.tier > 3);
    const warmupQuestions = warmupPool.slice(0, Math.min(2, warmupPool.length));
    const mainQuestions = mainPool.slice(0, Math.max(0, config.targetCount - warmupQuestions.length));
    return [...warmupQuestions, ...mainQuestions];
  }

  // Select target count
  return candidates.slice(0, config.targetCount);
}

/**
 * Challenge Mode: Unlock new content
 * Progressive difficulty within a family
 */
export function getChallengeQuestions(config: ChallengeConfig, allQuestions: Question[], state: AppState, selectedGroup: GroupId): Question[] {
  const rng = createRng(Date.now());
  // E-SCAN fix: use group-scoped key (was bare familyId → always undefined)
  const masteryKey = getFamilyMasteryKey(config.targetFamilyId, selectedGroup);
  const familyMastery = state.familyMastery?.[masteryKey];

  // §4.3: Use shared family+group filter
  let candidates = filterByFamilyAndGroup(allQuestions, config.targetFamilyId, selectedGroup);
  if (candidates.length === 0) {
    throw new Error(`No questions found for family ${config.targetFamilyId}`);
  }

  // Determine tier range based on difficulty and current mastery
  const attempts = familyMastery?.attempts ?? 0;
  const currentTier = attempts > 0 ? Math.floor((familyMastery?.masteryScore ?? 0) / 20) + 1 : 1;
  const tierOffset = config.difficulty - 1; // 0, 1, or 2
  const minTier = Math.max(1, currentTier + tierOffset);
  const maxTier = Math.min(16, minTier + 2);

  // §4.3: Use shared tier filter
  candidates = filterByTierRange(candidates, minTier, maxTier);

  // Sort by tier (progressive difficulty)
  candidates = candidates.slice().sort((a, b) => a.tier - b.tier);

  // Select 10-15 questions (deterministic)
  const targetCount = 10 + Math.floor(rng.random() * 6);
  return candidates.slice(0, Math.min(targetCount, candidates.length));
}

/**
 * Experiments Mode: Special question types and features
 * For testing new content or specific practice
 */
export function getExperimentsQuestions(config: ExperimentsConfig, allQuestions: Question[], state: AppState, selectedGroup: GroupId): Question[] {
  const rng = createRng(Date.now());

  // §4.3: Use shared group filter
  let candidates = filterByGroup(allQuestions, selectedGroup);

  // Apply experiment-specific filters
  switch (config.experimentType) {
    case 'visual_aids':
      // Questions with visual aids
      candidates = candidates.filter(q => q.stimulusAids && q.stimulusAids.length > 0 || q.supportAids && q.supportAids.length > 0);
      break;
    case 'interactive':
      // Interactive question types
      candidates = candidates.filter(q => q.questionType && !['multiple_choice'].includes(q.questionType));
      break;
    case 'new_content':
      // Recently added questions (last 30 days)
      // Show questions the user has NEVER attempted, or hasn't attempted in over 30 days
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      candidates = candidates.filter(q => {
        const questionState = state.questionStates[q.id];
        // Never attempted = new content ✅
        // Last attempted MORE than 30 days ago = effectively fresh content ✅
        return !questionState || questionState.lastAttemptDate < thirtyDaysAgo;
      });
      break;
    case 'mixed':
    default:
      // Random selection
      break;
  }

  // Shuffle and select 15-20 questions (unbiased Fisher-Yates)
  const shuffled = shuffle(candidates, rng);
  const targetCount = 15 + Math.floor(rng.random() * 6);
  return shuffled.slice(0, Math.min(targetCount, candidates.length));
}